import { useEffect, useMemo, useRef } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { useQuery } from '@tanstack/react-query';
import { fetchCountryRegions } from '@worldcoo/monolith/forms/regions';
import { NativeSelect, NativeSelectOptGroup, NativeSelectOption } from '../native-select';
import { cn } from '../../lib/utils';

export type NativeProvinceFieldTexts = {
	placeholder: string;
};

/**
 * The province rendered as a real <select> so browsers can autofill it — for Spain the
 * `address-level1` token carries the provincia ("Barcelona"), which is what this field holds.
 *
 * Two details make autofill actually land:
 *
 * - The option values are province *names*, not the numeric codes the API uses. A browser fills a
 *   <select> by matching its value against the options, so it has to be able to recognise one.
 *   The code stays the form value; this component translates at the edge.
 * - While no region is chosen the list holds every province in the country, grouped by region.
 *   A browser fills the province knowing nothing about comunidades autónomas, so the option has to
 *   exist for it to match. Once a region is set — including one derived from an autofilled
 *   province — the list narrows to that region, which is the short list users actually want.
 *
 * Because the value is the name, it cannot identify the province on its own: names repeat inside a
 * country, both across regions and within one. So a change is resolved by `selectedIndex` against
 * the options in render order, which is exact.
 *
 * The region is reported back through `onRegionChange`, which runs for a manual pick and an
 * autofill alike: a filled <select> fires `change` either way.
 */
export default function NativeProvinceField({
	label,
	country,
	region,
	className,
	texts,
	optional,
	autoComplete,
	onRegionChange,
}: {
	label: string;
	country?: string | undefined;
	region?: string | undefined;
	className?: string;
	texts: NativeProvinceFieldTexts;
	optional?: boolean;
	autoComplete?: string;
	onRegionChange?: (regionCode: string) => void;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);

	const prevCountryRef = useRef(country);
	useEffect(() => {
		if (prevCountryRef.current !== country) {
			prevCountryRef.current = country;
			field.handleChange('');
		}
	}, [country, field]);

	const { data: regions = [], isSuccess } = useQuery({
		queryKey: ['countries', country],
		queryFn: !country ? () => [] : () => fetchCountryRegions(country).then((data) => data ?? []),
	});

	const hasNoRegions = isSuccess && regions.length === 0;

	const nameByCode = useMemo(() => {
		const map = new Map<string, string>();
		for (const r of regions) for (const p of r.provinces) map.set(p.code, p.name);
		return map;
	}, [regions]);

	const selectedRegion = regions.find((r) => r.code === region);

	// The regions whose provinces are listed: just the selected one, or the whole country while
	// none is chosen. Both the options below and `flatOptions` are built from this single list, so
	// they cannot drift out of order.
	const listedRegions = useMemo(() => (selectedRegion ? [selectedRegion] : regions), [selectedRegion, regions]);

	// The options in render order. Looking a province up by its name would pick the wrong one
	// whenever a name repeats — "Calhoun County" occurs in 11 US states, "Merkez" in 38 Turkish
	// ones, and 42 of the ~150 countries we serve have at least one repeat — silently storing
	// another province's code and, through `onRegionChange`, moving the donor to another region.
	const flatOptions = useMemo(
		() => listedRegions.flatMap((r) => r.provinces.map((p) => ({ code: p.code, regionCode: r.code }))),
		[listedRegions],
	);

	// `selectedIndex` is 0 for the placeholder and then follows document order across optgroups,
	// so it indexes `flatOptions` shifted by one.
	const handleChange = (selectedIndex: number) => {
		const option = flatOptions[selectedIndex - 1];
		field.handleChange(option?.code ?? '');
		if (option) onRegionChange?.(option.regionCode);
	};

	return (
		<div className={cn('flex flex-col gap-2', className, (!country || hasNoRegions) && 'hidden')}>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<NativeSelect
				className="w-full"
				id={field.name}
				name={field.name}
				autoComplete={autoComplete}
				aria-invalid={hasFieldError(field.state.meta)}
				disabled={formReadOnly}
				value={nameByCode.get(field.state.value ?? '') ?? ''}
				onBlur={field.handleBlur}
				onChange={(event) => handleChange(event.target.selectedIndex)}
			>
				<NativeSelectOption value="">{texts.placeholder}</NativeSelectOption>
				{/* Iterates `listedRegions` exactly like `flatOptions` does, so the nth option here is
				    always the nth entry there. Once narrowed to one region the optgroup is dropped:
				    its label would just repeat the region already chosen above. */}
				{listedRegions.map((r) => {
					const provinces = r.provinces.map((province) => (
						<NativeSelectOption key={province.code} value={province.name}>
							{province.name}
						</NativeSelectOption>
					));
					if (selectedRegion) return provinces;
					return (
						<NativeSelectOptGroup key={r.code} label={r.name}>
							{provinces}
						</NativeSelectOptGroup>
					);
				})}
			</NativeSelect>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
